import React, { useEffect, useState, useCallback } from 'react';

/* ------------------------------------------------------------------ */
/*  MOCKUP IMAGE with placeholder fallback                             */
/* ------------------------------------------------------------------ */
function MockupImage({ src, alt, className, style }: { src: string; alt: string; className?: string; style?: React.CSSProperties }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={`aspect-[9/19] rounded-[2.5rem] bg-muted flex items-center justify-center ${className ?? ''}`}
        style={{ boxShadow: '0 40px 80px rgba(0,0,0,0.12)', ...style }}
      >
        <span className="text-xs text-muted-foreground">App mockup coming</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={() => setFailed(true)}
    />
  );
}
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { APP_NAME } from '@/lib/branding';
import { Button } from '@/components/ui/button';
import { ArrowRight, TrendingDown, RefreshCw, Compass, Target, PieChart, Sparkles, Star, ChevronDown } from 'lucide-react';
import SEO from '@/components/SEO';

/* ------------------------------------------------------------------ */
/*  DATA                                                               */
/* ------------------------------------------------------------------ */

const SOCIAL_PROOF_ITEMS = [
  '1,200+ women already on the waitlist',
  'Rated 4.9/5 by beta users',
  'Featured in Forbes',
  '87% of early users felt clearer about money within their first week',
];

const PROBLEM_CARDS = [
  {
    emoji: '🌀',
    title: 'The money disappears',
    body: "You check your balance and somehow it's lower than it should be — and you genuinely don't know why.",
  },
  {
    emoji: '📋',
    title: 'The apps never stick',
    body: "You've downloaded the budgeting apps. Started the spreadsheet. Tried the challenges. None of it lasted past week three.",
  },
  {
    emoji: '🔍',
    title: "You don't know your next step",
    body: "You know you should be doing something — saving more, investing, something — but nobody has ever told you exactly what, in the right order, for where you actually are.",
  },
];

const WAVE_GOODBYE_ITEMS = [
  '… not knowing where your money goes each month',
  '… starting over again every January',
  '… dreading the end of the month',
  '… feeling guilty after every non-essential purchase',
  '… apps that make you feel worse, not better',
  "… financial advice written for someone else's life",
  '… saving in theory but never in practice',
  '… that low-level anxiety every time your phone pings from your bank',
];

const STEPS = [
  {
    num: '01',
    title: 'Take the 2-minute quiz',
    body: "Answer a few honest questions about where you are right now. No right or wrong answers. WealthTimeline places you in your money stage — Seed Planter, Budget Builder, Wealth Grower, or Financially Free — so everything you see is built for you.",
  },
  {
    num: '02',
    title: 'Get 7 days of free app access',
    body: "Begin building your new money habit from day one. Calm daily prompts, smart AI nudges, and a dashboard that makes sense. No overwhelm. Just quiet, consistent progress. A paid subscription is charged in full when you choose to upgrade.",
  },
  {
    num: '03',
    title: 'Get your personalised plan',
    body: "See your Wants, Needs, and Freedom breakdown at a glance. Understand exactly where your money is going, how aligned it is with what you actually want, and what your one clear next step is — no more guessing.",
  },
];

const FEATURES = [
  {
    emoji: '📍',
    title: 'Money Stage System',
    body: "You're placed in one of four stages — Seed Planter through to Financially Free — so every prompt, insight, and action you see is matched to your current reality, not someone else's.",
  },
  {
    emoji: '⚖️',
    title: 'Wants / Needs / Freedom Clarity Model',
    body: "Every transaction flows into a simple three-part breakdown, so you can see, in seconds, whether your spending reflects what you actually value — and what to shift if it doesn't.",
  },
  {
    emoji: '✨',
    title: 'AI Insights & Nudges',
    body: "Your AI coach spots patterns you'd miss, flags when things are drifting, and surfaces the one thing that matters most this week — without drowning you in data or making you feel judged.",
  },
  {
    emoji: '🎯',
    title: 'Goals & Wishlist Tracker',
    body: "Link your goals — emergency fund, dream holiday, first investment — to your daily choices. Watch real progress unfold with timelines, milestones, and small celebrations along the way.",
  },
];

const STATS = [
  { value: '87%', label: 'of users felt clearer about money after their first week' },
  { value: '2 min', label: 'is all it takes to find your money stage and get your personalised plan' },
  { value: '94%', label: "of beta users said it felt nothing like other money apps they'd tried" },
  { value: '7 days', label: 'is the average number of days before users hit their first savings milestone' },
];

const TESTIMONIALS = [
  {
    quote: "I never thought I'd actually stick with something like this. I'd tried apps before and always felt overwhelmed or judged. WealthTimeline was the first time I understood where my money was going — and felt genuinely okay about starting where I was.",
    name: '[Name]',
    job: '[Job title]',
    city: '[City]',
    stage: 'Seed Planter',
    initial: 'N',
  },
  {
    quote: "I knew what I should be doing — I just never stuck to it. Having one clear next step instead of a to-do list of ten things changed everything. For the first time I didn't feel like I was failing.",
    name: '[Name]',
    job: '[Job title]',
    city: '[City]',
    stage: 'Budget Builder',
    initial: 'S',
  },
  {
    quote: "I earn well but I've always had this nagging feeling my money could be doing more. WealthTimeline gave me a real picture of where things stood — and showed me exactly what to focus on first. I didn't know clarity could feel this calm.",
    name: '[Name]',
    job: '[Job title]',
    city: '[City]',
    stage: 'Wealth Grower',
    initial: 'A',
  },
];

const FAQ_ITEMS = [
  {
    q: 'What is a money stage — and how do I find mine?',
    a: "Your money stage reflects where you genuinely are in your financial life right now — not where you think you should be. There are four stages: Seed Planter, Budget Builder, Wealth Grower, and Financially Free. Each one unlocks a different set of prompts, focus areas, and guidance tailored to your current situation. You find yours by taking the free 2-minute quiz.",
  },
  {
    q: 'What does the quiz involve?',
    a: "It's seven short questions about your current money habits, goals, and how you feel about your finances. There are no right or wrong answers — it's designed to understand you, not test you. It takes about two minutes and gives you an instant result.",
  },
  {
    q: "What do I get with 7 days of free app access?",
    a: "You can explore your personalised dashboard, Wants/Needs/Freedom breakdown, goals tracker, AI nudges, and stage-matched plan for 7 days without entering payment details. If you choose to upgrade, the full annual subscription price is charged immediately at checkout.",
  },
  {
    q: 'How much does WealthTimeline cost?',
    a: "See the pricing page for current plan prices. If you choose to upgrade, the full annual subscription price is charged immediately at checkout; the 7 days of free app access do not defer that payment.",
  },
  {
    q: 'Is my financial data safe?',
    a: "Yes. WealthTimeline is built on secure, encrypted infrastructure. Your data is never sold, shared, or used for advertising. You enter what you choose to enter — there's no bank account connection required to get started.",
  },
  {
    q: "What happens after my 7 days of free app access?",
    a: "You'll need a paid subscription to continue using the full set of features. Your data stays intact, and you can choose whether to upgrade. Upgrading starts a paid annual subscription charged at checkout.",
  },
];

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
};

/* ------------------------------------------------------------------ */
/*  MARQUEE COMPONENT                                                  */
/* ------------------------------------------------------------------ */

function Marquee({
  children,
  reverse = false,
  className = '',
}: {
  children: React.ReactNode;
  reverse?: boolean;
  className?: string;
}) {
  return (
    <div className={`overflow-hidden ${className}`} aria-hidden="true">
      <div
        className={`flex w-max gap-6 ${reverse ? 'animate-marquee-reverse' : 'animate-marquee'}`}
      >
        {children}
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ SECTION (useState accordion)                                   */
/* ------------------------------------------------------------------ */

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: '#f7f4f0' }} aria-labelledby="faq-title">
      <div className="mx-auto max-w-2xl">
        <h2 id="faq-title" className="font-serif text-3xl md:text-5xl font-bold text-center mb-4" style={{ color: '#1a1a1a' }}>
          Got questions? Good.
        </h2>
        <p className="text-lg text-center max-w-2xl mx-auto mb-14" style={{ color: '#888' }}>&nbsp;</p>
        {FAQ_ITEMS.map((item, i) => (
          <div key={i} style={{ borderBottom: '1px solid #e5e2dd' }}>
            <button
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              className="w-full flex justify-between items-center py-5 text-left font-semibold hover:opacity-70 transition-opacity"
              style={{ color: '#111' }}
            >
              <span>{item.q}</span>
              <ChevronDown
                className="w-5 h-5 shrink-0 ml-4 transition-transform duration-200"
                style={{ color: '#999', transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>
            {openIndex === i && (
              <p className="leading-relaxed pb-5" style={{ color: '#888' }}>{item.a}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  LANDING PAGE                                                       */
/* ------------------------------------------------------------------ */

export default function Landing() {
  const { user, loading } = useAuth();

  useEffect(() => {
    document.title = `${APP_NAME} — Build mindful money habits`;
  }, []);

  if (loading) {
    return (
      <div role="status" aria-label="Loading" aria-busy="true" style={{ padding: 16 }}>
        Loading…
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <main id="content" className="overflow-x-hidden">
      <SEO
        title="WealthTimeline — Build mindful money habits"
        description="Track spending across Wants, Needs & Freedom. Take a 2-minute quiz to discover your financial persona and get 7 days of free app access."
        path="/"
      />

      {/* FAQ JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      {/* ============================================================ */}
      {/* S1 — HERO                                                     */}
      {/* ============================================================ */}
      <section
        className="relative overflow-hidden"
        aria-labelledby="hero-title"
        style={{ backgroundColor: '#eeeae4' }}
      >
        {/* Mesh gradient blobs */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
          {/* Left purple blob */}
          <div style={{
            position: 'absolute',
            width: '65%',
            height: '130%',
            top: '-15%',
            left: '-20%',
            background: 'radial-gradient(ellipse, rgba(160, 100, 210, 0.60) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }} />
          {/* Right coral blob */}
          <div style={{
            position: 'absolute',
            width: '65%',
            height: '130%',
            top: '-15%',
            right: '-20%',
            background: 'radial-gradient(ellipse, rgba(225, 130, 95, 0.55) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }} />
          {/* Bottom centre warmth */}
          <div style={{
            position: 'absolute',
            width: '50%',
            height: '60%',
            bottom: '-10%',
            left: '25%',
            background: 'radial-gradient(ellipse, rgba(210, 160, 180, 0.25) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }} />
          {/* Grain overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            opacity: 0.08,
            mixBlendMode: 'overlay' as const,
          }} />
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-6 pt-28 pb-24 sm:pt-36 sm:pb-32 flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="text-center md:text-left md:max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-6">
              You're not bad with money.
            </p>
            <h1
              id="hero-title"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-foreground mb-8"
            >
              You just haven't had a system built for where you actually are.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto md:mx-0 mb-10 leading-relaxed">
              You earn decent money. You work hard. But at the end of the month, it's the same quiet
              panic — where did it all go? WealthTimeline gives you a personalised money stage, a
              clear plan that fits your real life, and one calm next step at a time. Start by finding
              out exactly where you stand.
            </p>
            <Button size="lg" asChild className="text-base h-14 px-10 rounded-full shadow-lg hover:shadow-xl transition-shadow">
              <Link to="/quiz">
                Find your money stage in 2 minutes
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
            <p className="mt-5 text-sm text-muted-foreground">
              Free to start. No credit card needed.
            </p>
          </div>
          <MockupImage
            src="/images/app-mockup-dashboard.png"
            alt="WealthTimeline dashboard showing your money stage and goals"
            className="w-full max-w-sm mx-auto md:mx-0 mt-12 md:mt-0 drop-shadow-2xl rounded-[2.5rem]"
            style={{ transform: 'rotate(-3deg)' }}
          />
        </div>
      </section>

      {/* ============================================================ */}
      {/* S2 — SOCIAL PROOF MARQUEE                                     */}
      {/* ============================================================ */}
      <section className="bg-foreground text-primary-foreground py-4">
        <Marquee>
          {SOCIAL_PROOF_ITEMS.map((item) => (
            <span key={item} className="whitespace-nowrap text-sm font-medium flex items-center gap-6">
              <span className="opacity-40">·</span>
              <span className="bg-primary-foreground/10 px-3 py-1 rounded-full">{item}</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ============================================================ */}
      {/* S3 — THE PROBLEM — #f7f4f0                                    */}
      {/* ============================================================ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: '#f7f4f0' }} aria-labelledby="problem-title">
        <div className="mx-auto max-w-6xl">
          <h2 id="problem-title" className="font-serif text-3xl md:text-5xl font-bold text-center mb-4" style={{ color: '#1a1a1a' }}>
            You're not alone.
          </h2>
          <p className="text-lg text-center max-w-2xl mx-auto mb-14" style={{ color: '#888' }}>
            You make good money. So why doesn't it feel that way?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { Icon: TrendingDown, iconBg: '#fff1f2', iconColor: '#f43f5e', title: 'The money disappears', body: PROBLEM_CARDS[0].body },
              { Icon: RefreshCw, iconBg: '#fffbeb', iconColor: '#f59e0b', title: 'The apps never stick', body: PROBLEM_CARDS[1].body },
              { Icon: Compass, iconBg: '#f5f3ff', iconColor: '#8b5cf6', title: "You don't know your next step", body: PROBLEM_CARDS[2].body },
            ].map((card) => (
              <div key={card.title} className="rounded-2xl p-8 shadow-sm" style={{ backgroundColor: '#ffffff' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: card.iconBg }}>
                  <card.Icon className="w-5 h-5" style={{ color: card.iconColor }} />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#111' }}>{card.title}</h3>
                <p className="leading-relaxed" style={{ color: '#888' }}>{card.body}</p>
              </div>
            ))}
          </div>
          <p className="text-center italic mt-10 text-lg" style={{ color: '#888' }}>
            You don't need more willpower. You need a system that actually fits your life.
          </p>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S4 — WAVE GOODBYE MARQUEE — dark                              */}
      {/* ============================================================ */}
      <section className="py-14 sm:py-16" style={{ backgroundColor: '#1a1a1a' }}>
        <div className="mx-auto max-w-4xl px-6 mb-8">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-center text-white">
            Wave goodbye to…
          </h2>
        </div>
        <Marquee>
          {WAVE_GOODBYE_ITEMS.map((item) => (
            <span
              key={item}
              className="whitespace-nowrap text-sm font-medium px-5 py-2.5 rounded-full"
              style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {item}
            </span>
          ))}
        </Marquee>
      </section>

      {/* ============================================================ */}
      {/* S5 — HOW IT WORKS — white                                     */}
      {/* ============================================================ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: '#ffffff' }} aria-labelledby="how-title">
        <div className="mx-auto max-w-6xl">
          <h2 id="how-title" className="font-serif text-3xl md:text-5xl font-bold text-center mb-4" style={{ color: '#1a1a1a' }}>
            Here's how it works.
          </h2>
          <p className="text-lg text-center max-w-2xl mx-auto mb-14" style={{ color: '#888' }}>
            Three steps to a money life that finally makes sense.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((step) => (
              <div key={step.num} className="rounded-2xl p-8 shadow-sm" style={{ backgroundColor: '#f9f8f6' }}>
                <span className="text-7xl font-bold leading-none mb-4 block" style={{ color: '#eae8e4' }}>{step.num}</span>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#111' }}>{step.title}</h3>
                <p className="leading-relaxed" style={{ color: '#888' }}>{step.body}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Button size="lg" asChild className="text-base h-14 px-10 rounded-full shadow-lg hover:shadow-xl transition-shadow">
              <Link to="/quiz">
                Find your money stage in 2 minutes
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S6 — FEATURES — #f7f4f0                                       */}
      {/* ============================================================ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: '#f7f4f0' }} aria-labelledby="features-title">
        <div className="mx-auto max-w-6xl">
          <h2 id="features-title" className="font-serif text-3xl md:text-5xl font-bold text-center mb-4" style={{ color: '#1a1a1a' }}>
            Built differently.
          </h2>
          <p className="text-lg text-center max-w-2xl mx-auto mb-14" style={{ color: '#888' }}>
            The tools you actually need. Nothing you don't.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { Icon: Target, iconBg: '#f0fdf4', iconColor: '#16a34a', title: 'Money Stage System', body: FEATURES[0].body },
              { Icon: PieChart, iconBg: '#eff6ff', iconColor: '#2563eb', title: 'Wants / Needs / Freedom', body: FEATURES[1].body },
              { Icon: Sparkles, iconBg: '#faf5ff', iconColor: '#9333ea', title: 'AI Insights & Nudges', body: FEATURES[2].body },
              { Icon: Star, iconBg: '#fff1f2', iconColor: '#e11d48', title: 'Goals & Wishlist Tracker', body: FEATURES[3].body },
            ].map((f) => (
              <div key={f.title} className="rounded-2xl p-8 shadow-sm" style={{ backgroundColor: '#ffffff' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: f.iconBg }}>
                  <f.Icon className="w-5 h-5" style={{ color: f.iconColor }} />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#111' }}>{f.title}</h3>
                <p className="leading-relaxed" style={{ color: '#888' }}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* MOCKUP SHOWCASE — goals iPad                                   */}
      {/* ============================================================ */}
      <div className="w-full bg-transparent py-8 md:py-12">
        <p className="text-xs font-medium tracking-widest uppercase text-center mb-6" style={{ color: '#999' }}>
          See it in action
        </p>
        <div className="relative w-full max-w-2xl mx-auto px-6">
          <MockupImage
            src="/images/app-mockup-goals.png"
            alt="WealthTimeline goals tracker"
            className="w-full"
            style={{ transform: 'rotate(-2deg)' }}
          />
        </div>
        <p className="text-sm text-center mt-6 max-w-sm mx-auto" style={{ color: '#999' }}>
          Your goals, your timeline — tracked in one calm view.
        </p>
      </div>

      {/* ============================================================ */}
      {/* S7 — STATS BAR — dark                                         */}
      {/* ============================================================ */}
      <section className="py-16" style={{ backgroundColor: '#1a1a1a' }}>
        <div className="mx-auto max-w-4xl px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {STATS.map((s, i) => (
              <div key={s.label} className={i > 0 ? 'md:border-l' : ''} style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-5xl font-bold text-white mb-2">{s.value}</p>
                <p className="text-sm mt-2" style={{ color: '#999' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S8 — TESTIMONIALS — light                                     */}
      {/* ============================================================ */}
      <section className="py-20 px-6" style={{ backgroundColor: '#f7f4f0' }} aria-labelledby="testimonials-title">
        <div className="mx-auto max-w-6xl">
          <h2 id="testimonials-title" className="font-serif text-4xl md:text-5xl font-bold text-center mb-14" style={{ color: '#111' }}>
            Real women. Real results.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => {
              const stageColor: Record<string, string> = {
                'Seed Planter': 'bg-green-50 text-green-600',
                'Budget Builder': 'bg-blue-50 text-blue-600',
                'Wealth Grower': 'bg-purple-50 text-purple-600',
              };
              return (
                <div key={t.stage} className="rounded-2xl p-8 shadow-sm border border-gray-100 flex flex-col justify-between" style={{ backgroundColor: '#ffffff' }}>
                  <p className="leading-relaxed italic text-base mb-6 flex-1" style={{ color: '#555' }}>
                    "{t.quote}"
                  </p>
                  <div className="border-t border-gray-100 pt-6 mt-auto">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-500">
                        {t.initial}
                      </div>
                      <div>
                        <p className="font-semibold text-sm" style={{ color: '#111' }}>{t.name}, {t.job}, {t.city}</p>
                        <span className={`inline-block mt-1 text-xs font-medium px-3 py-1 rounded-full ${stageColor[t.stage] ?? ''}`}>
                          {t.stage}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S9 — FOUNDER STORY — white                                    */}
      {/* ============================================================ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20" style={{ backgroundColor: '#ffffff' }} aria-labelledby="founder-title">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest mb-6" style={{ color: '#999' }}>Why I built this.</p>
              <div className="space-y-5" style={{ color: '#555' }}>
                <p className="leading-relaxed">
                  Nobody taught me about money growing up. My parents were immigrants — they were
                  busy just trying to make ends meet. I'm now a senior tech consultant and a mum of
                  two young daughters, and I've spent years learning the hard way what nobody ever
                  sat me down and explained.
                </p>
                <p className="leading-relaxed">
                  When I looked around, I saw so many women in the same place — smart, capable,
                  earning good money, but still feeling behind. Not because they were doing anything
                  wrong. Because they'd never had the right system, in the right language, built for
                  where they actually were.
                </p>
                <p className="leading-relaxed">
                  WealthTimeline is the thing I wish I'd had ten years ago. I built it for you — and
                  for my daughters, so they never have to start from scratch.
                </p>
              </div>
              <p className="mt-8 font-semibold" style={{ color: '#111' }}>
                — Raluca, Founder of WealthTimeline
              </p>
            </div>
            <div className="rounded-2xl aspect-square w-full overflow-hidden">
              <img src="/images/founder-photo.jpg" alt="Raluca, Founder of WealthTimeline" className="w-full h-full object-cover rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* S10 — FAQ — #f7f4f0                                           */}
      {/* ============================================================ */}
      <FAQSection />

      {/* ============================================================ */}
      {/* S11 — FINAL CTA BANNER — dark                                 */}
      {/* ============================================================ */}
      <section className="py-24" style={{ backgroundColor: '#1a1a1a' }}>
        <div className="mx-auto max-w-2xl px-6 text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-white text-center mb-6 max-w-2xl mx-auto">
            You already know something needs to change.
          </h2>
          <p className="text-lg text-center mb-10 max-w-xl mx-auto" style={{ color: '#999' }}>
            You don't need to overhaul everything. You don't need to be a finance expert. You just
            need to know where you are — and what to do next.
          </p>
          <Button
            size="lg"
            asChild
            className="text-base h-14 px-10 rounded-full shadow-lg hover:bg-gray-100"
            style={{ backgroundColor: '#ffffff', color: '#1a1a1a' }}
          >
            <Link to="/quiz">
              Get started for free
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
          <p className="text-sm mt-4 text-center" style={{ color: '#666' }}>
            7 days of free app access. A paid subscription is charged immediately when you upgrade.
          </p>
        </div>
      </section>
    </main>
  );
}
